import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPResourceSecurityHelpModal from "shared/components/app/CPResourceSecurityHelpModal/CPResourceSecurityHelpModal";
import CPHelpButton from "shared/components/shared/CPHelpButton/CPHelpButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPTextArea from "shared/components/shared/CPTextArea/CPTextArea";
import CPCheckbox from "shared/components/shared/CPCheckbox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import useAppState from "shared/state/useAppState";

import {
  useGetCategoriesForResource,
  useGetRolesForResource,
} from "data/resources/resource";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import {
  LinkResource,
  useUpsertLinkMutation,
  useDeleteLinkMutation,
} from "data/resources/link";
import { ResourceCategory, ResourceRole } from "data/resources/resource";
import CPSpacer from "shared/components/shared/CPSpacer/CPSpacer";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";

export class UpsertLinkRecord implements Omit<LinkResource, "agencies"> {
  id: number = -1;
  title: string = "";
  url: string = "";
  categories: string[] = [];
  roles: string[] = [];
  summary?: string = "";
  resourceTier: string = "";
  isActive: boolean = true;
  mode?: "NONE" | "WINDOW" | "TAB" = "NONE";
}

interface LinkDetailsModalProps {
  selectedLink: UpsertLinkRecord | null;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
}

const LinkDetailsModal: React.FC<LinkDetailsModalProps> = ({
  selectedLink,
  onClose,
  onSave,
  onDelete,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const confirm = useConfirm();
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesForResource(selectedLink?.id ?? -1);
  const { data: roles, isLoading: rolesLoading } = useGetRolesForResource(
    selectedLink?.id ?? -1,
  );
  const {
    mutate,
    isLoading: isSaving,
    error: saveError,
  } = useUpsertLinkMutation();
  const { mutate: deleteLink, isLoading: isDeleting } = useDeleteLinkMutation();
  const [editLink, setEditLink] = React.useState<UpsertLinkRecord | null>({
    ...selectedLink,
  } as UpsertLinkRecord);

  const [categoryOptions, setCategoryOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [roleOptions, setRoleOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [showSecurityHelp, setShowSecurityHelp] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(categoriesLoading || rolesLoading || isSaving);
  }, [categoriesLoading, rolesLoading, isSaving, setIsLoading]);

  React.useEffect(() => {
    if (categories) {
      setCategoryOptions({
        available: categories.available.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
        selected: categories.assigned.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
      });
    }
  }, [categories]);

  React.useEffect(() => {
    if (roles) {
      setRoleOptions({
        available: roles.available.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
        selected: roles.assigned.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
      });
    }
  }, [roles]);

  // When a child category is selected, move the paren as well.
  const handleCategoryChange = React.useCallback(
    (options: CPBoxOption) => {
      if (!categories) {
        setCategoryOptions(options);
        return;
      }

      const allCategories = [...categories.available, ...categories.assigned];

      const getAncestorIds = (name: string): string[] => {
        const cat = allCategories.find((c) => c.name === name);
        if (!cat?.parentName) return [];
        const parent = allCategories.find((c) => c.name === cat.parentName);
        if (!parent) return [];
        return [parent.name, ...getAncestorIds(parent.name)];
      };

      const selectedIds = new Set(options.selected.map((o) => o.value));
      const ancestorIds = new Set(
        options.selected.flatMap((o) => getAncestorIds(o.value as string)),
      );

      const autoPromoted = options.available
        .filter(
          (o) =>
            ancestorIds.has(o.value as string) &&
            !selectedIds.has(o.value as string),
        )
        .map((o) => ({ ...o, selected: false }));

      const allRequiredIds = new Set(
        Array.from(selectedIds).concat(Array.from(ancestorIds)),
      );

      setCategoryOptions({
        available: options.available.filter(
          (o) => !allRequiredIds.has(o.value as string),
        ),
        selected: [...options.selected, ...autoPromoted],
      });
    },
    [categories],
  );

  const handleSave = () => {
    if (!editLink) return;
    mutate(
      {
        ...editLink,
        categories: categoryOptions.selected.map((c) => c.value as string),
        roles: roleOptions.selected.map((r) => r.value as string),
      },
      {
        onSuccess: () => {
          setToastMsg("Link saved successfully", "success");
          onSave();
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }

          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  const handleDelete = () => {
    if (!editLink) return;
    confirm((confirmed) => {
      if (confirmed) {
        deleteLink(editLink.id, {
          onSuccess: () => {
            setToastMsg("Link deleted successfully", "success");
            onClose();
            onDelete?.();
          },
          onError: () => {
            setToastMsg("Failed to delete link", "error");
          },
        });
      }
    }, `Are you sure you want to delete "${editLink.title}"?`);
  };

  const isNew = selectedLink?.id === -1;

  return (
    <CPActionsheetModal
      title={isNew ? "Add Link" : "Edit Link"}
      open={selectedLink !== null && !rolesLoading && !categoriesLoading}
      onClose={onClose}
      position="right"
      width={550}
      actionBtns={
        <Box spacing={8}>
          {!isNew && (
            <CPButton
              themeColor="error"
              fillMode="outline"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Delete
            </CPButton>
          )}
          <CPButton
            themeColor="primary"
            onClick={handleSave}
            disabled={isDeleting}
          >
            Save
          </CPButton>
        </Box>
      }
    >
      {selectedLink && (
        <Box spacing={24} flexDirection="column" style={{ paddingBottom: 16 }}>
          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                toCapital
                required
                name="title"
                label="Link Title"
                value={editLink?.title}
                error={saveError}
                onChange={(e) => {
                  setEditLink(
                    (prev) =>
                      ({
                        ...prev,
                        title: e.target.value,
                      }) as UpsertLinkRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                required
                name="url"
                label="URL"
                error={saveError}
                helpText="A fully qualified URL is required, including the protocol (e.g. https://example.com)"
                value={editLink?.url}
                onChange={(e) => {
                  setEditLink(
                    (prev) =>
                      ({
                        ...prev,
                        url: e.target.value,
                      }) as UpsertLinkRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPCheckbox
                name="isActive"
                label="Active"
                checked={editLink?.isActive}
                onChange={(e) => {
                  setEditLink(
                    (prev) =>
                      ({
                        ...prev,
                        isActive: e.value,
                      }) as UpsertLinkRecord,
                  );
                }}
              />
              <CPText>Show or hide this link</CPText>
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPTextArea
                name="summary"
                label="Summary"
                error={saveError}
                disableResize
                value={editLink?.summary}
                rows={4}
                onChange={(e) => {
                  setEditLink(
                    (prev) =>
                      ({
                        ...prev,
                        summary: e.value,
                      }) as UpsertLinkRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPDropDownList
                name="mode"
                label="View Mode"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultValue={"NONE"}
                data={[
                  { label: "Same window", value: "NONE" },
                  { label: "New window", value: "WINDOW" },
                  { label: "New tab", value: "TAB" },
                ]}
                value={editLink?.mode ?? "NONE"}
                onChange={(e) => {
                  setEditLink(
                    (prev) =>
                      ({
                        ...prev,
                        mode: e.value,
                      }) as UpsertLinkRecord,
                  );
                }}
              />
            </Box>
          </Box>

          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                spacing={10}
              >
                <Box flexDirection="column" spacing={4}>
                  <CPText
                    textStyle="h6"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="fa-solid fa-lock"
                      style={{ marginRight: 8 }}
                    />
                    Access & Security
                  </CPText>
                  <CPText style={{ opacity: 0.7 }}>
                    Control who can access this resource
                  </CPText>
                </Box>
                <CPHelpButton
                  onClick={() => setShowSecurityHelp(true)}
                  title="How does security work?"
                  aria-label="Visibility help"
                />
              </Box>
              <CPSpacer height={15} />
              <Box>
                <CPText textStyle="subtitle" style={{ fontSize: 13 }}>
                  Resource Tier
                </CPText>
                <CPText
                  textStyle="caption"
                  style={{ opacity: 0.8, fontSize: 12, marginTop: 4 }}
                >
                  Lock the resource to agencies with matching tier access.
                </CPText>
              </Box>

              <CPSpacer height={8} />

              <CPDropDownList
                name="resourceTier"
                label="Tier"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultItem={{ label: "Please choose...", value: null }}
                data={[
                  { label: "Bronze", value: "BRONZE" },
                  { label: "Silver", value: "SILVER" },
                  { label: "Gold", value: "GOLD" },
                ]}
                value={editLink?.resourceTier ?? null}
                onChange={(e) => {
                  setEditLink(
                    (prev) =>
                      ({
                        ...prev,
                        resourceTier: e.value,
                      }) as UpsertLinkRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="categories"
                headerText="Assigned Categories *"
                filterable
                boxOptions={categoryOptions}
                onChange={handleCategoryChange}
                error={saveError}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="roles"
                headerText="Assigned Roles"
                filterable
                boxOptions={roleOptions}
                onChange={(options) => setRoleOptions(options)}
              />
            </Box>
          </Box>
        </Box>
      )}

      <CPResourceSecurityHelpModal
        open={showSecurityHelp}
        onClose={() => setShowSecurityHelp(false)}
      />
    </CPActionsheetModal>
  );
};

export default LinkDetailsModal;
